import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import { Pill } from '@/components/widgets/Pill';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/sonner';
import { adminMainAccount, adminRealAccount, adminSymbols } from '@/api/admin';
import type { CloseRealPositionsRequest, CloseRealPositionsResponse } from '@/api/types';
import { fmtMoney, fmtPnLNumber, fmtTime } from '@/lib/format';
import { ApiError } from '@/lib/http';
import { cn } from '@/lib/utils';
import { useLiveQuote, usePriceFlash } from '@/realtime/use-quotes';
import { computeNetPnl, computePositionLive } from './position-pnl';

export interface MainPosition {
  positionId: string;
  /** 品种数字 ID(字符串)— 用于订阅实时行情、本地算现价/浮盈 */
  symbolId: string;
  side: 'BUY' | 'SELL';
  volume: number; // centi-lot (0.01 lot)
  openPrice: number;
  sl?: number;
  tp?: number;
  openedAt: string;
  source?: string;
  /** 库存费(美元,cTrader 符号,成本为负)— 用于「净盈亏」列 */
  swap?: number;
  /** 佣金(美元,cTrader 符号,成本为负)— 用于「净盈亏」列 */
  commission?: number;
}

interface Props {
  positions: MainPosition[];
}

/** 价格跳动时的淡色高亮背景(emerald/red,约等于设计 token 的 up/down 色) */
const flashBg = (f: 'up' | 'down' | null): string =>
  f === 'up' ? 'rgba(16,185,129,0.18)' : f === 'down' ? 'rgba(239,68,68,0.18)' : 'transparent';

/** 「真实账户平仓」弹窗的目标:全部真实仓 / 指定一笔 */
type CloseTarget = { scope: 'all' } | { scope: 'position'; position: MainPosition };

/**
 * 主账户当前持仓表 — §9.4 v1.1: 列表(支持多条)。
 * 现价 / 浮动 PnL 由每行订阅实时行情(quote WS)本地计算,逐 tick 跳动。
 * 「浮动 PnL」是毛价差浮盈(不含费用);「净盈亏」= 浮动 PnL + 库存费 + 佣金,
 * 与 cTrader 客户端「净盈亏」口径一致(见 position-pnl.ts::computeNetPnl)。
 *
 * 「真实账户平仓」(表头「平掉全部真实仓」/ 行尾「平掉此仓」)是人工平真实仓 + 全量脱钩:
 * 不置 emergency_stop、不锁交易、不进强平编排,系统继续运行 —— 与顶部「一键强制平仓」是
 * 完全不同的两件事,不要混用。
 */
export function PositionsTable({ positions }: Props) {
  const [target, setTarget] = useState<CloseTarget | null>(null);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-3">
            <span>主账户当前持仓 · {positions.length} 笔</span>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={() => setTarget({ scope: 'all' })}
              disabled={positions.length === 0}
              title="人工平掉真实账户全部仓位,并把此刻全部存量用户持仓标记为已脱钩;不停机、不锁交易"
            >
              <ShieldAlert className="h-4 w-4" />
              平掉全部真实仓
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg-3 text-ink-2 text-xs">
                <tr>
                  <th className="px-3 py-2 text-left font-normal">订单号</th>
                  <th className="px-3 py-2 text-left font-normal">方向</th>
                  <th className="px-3 py-2 text-right font-normal">手数</th>
                  <th className="px-3 py-2 text-right font-normal">开仓价</th>
                  <th className="px-3 py-2 text-right font-normal">当前价</th>
                  <th className="px-3 py-2 text-right font-normal">SL</th>
                  <th className="px-3 py-2 text-right font-normal">TP</th>
                  <th className="px-3 py-2 text-left font-normal">开仓时间</th>
                  <th className="px-3 py-2 text-left font-normal">来源</th>
                  <th
                    className="px-3 py-2 text-right font-normal"
                    title="毛价差浮盈,不含库存费/佣金(逐 tick 本地计算)"
                  >
                    浮动 PnL
                  </th>
                  <th
                    className="px-3 py-2 text-right font-normal"
                    title="净盈亏 = 浮动 PnL + 库存费(swap) + 佣金(commission),与 cTrader「净盈亏」口径一致"
                  >
                    净盈亏
                  </th>
                  <th className="px-3 py-2 text-right font-normal">操作</th>
                </tr>
              </thead>
              <tbody>
                {positions.length === 0 && (
                  <tr>
                    <td colSpan={12} className="px-3 py-8 text-center text-ink-3">
                      暂无持仓
                    </td>
                  </tr>
                )}
                {positions.map((p) => (
                  <PositionRow key={p.positionId} p={p} onClose={(pos) => setTarget({ scope: 'position', position: pos })} />
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <CloseRealPositionsDialog target={target} onDismiss={() => setTarget(null)} />
    </>
  );
}

/** 单行持仓 — 订阅自己品种的实时行情,逐 tick 算现价/浮盈并闪烁。 */
function PositionRow({ p, onClose }: { p: MainPosition; onClose: (p: MainPosition) => void }) {
  const quote = useLiveQuote(p.symbolId);
  const { currentPrice, unrealizedPnl } = computePositionLive(
    { side: p.side, volume: p.volume, entryPrice: p.openPrice },
    quote,
  );
  // 净盈亏 = 毛浮动 PnL + 库存费(swap) + 佣金(commission)。行情未到时(unrealizedPnl 为空)一并留空。
  const netPnl = computeNetPnl(unrealizedPnl, p.swap, p.commission);
  const fees = (p.swap ?? 0) + (p.commission ?? 0);
  const flash = usePriceFlash(currentPrice);

  return (
    <tr className="border-t border-line row-hover">
      <td className="px-3 py-2 num text-ink-2">{p.positionId}</td>
      <td className="px-3 py-2">
        <Pill tone={p.side === 'BUY' ? 'up' : 'down'}>{p.side}</Pill>
      </td>
      <td className="px-3 py-2 text-right num">{(p.volume / 100).toFixed(2)}</td>
      <td className="px-3 py-2 text-right num">{p.openPrice.toFixed(2)}</td>
      <td
        className="px-3 py-2 text-right num font-medium"
        style={{ backgroundColor: flashBg(flash), transition: 'background-color 300ms ease-out' }}
      >
        {currentPrice != null ? currentPrice.toFixed(2) : '—'}
      </td>
      <td className="px-3 py-2 text-right num text-ink-3">{p.sl?.toFixed(2) ?? '—'}</td>
      <td className="px-3 py-2 text-right num text-ink-3">{p.tp?.toFixed(2) ?? '—'}</td>
      <td className="px-3 py-2 num text-xs text-ink-2">{fmtTime(p.openedAt)}</td>
      <td className="px-3 py-2 text-xs text-ink-2">{p.source ?? '—'}</td>
      {unrealizedPnl != null ? (
        <td className={cn('px-3 py-2 text-right num font-medium', unrealizedPnl >= 0 ? 'text-up' : 'text-down')}>
          {fmtPnLNumber(unrealizedPnl)}
        </td>
      ) : (
        <td className="px-3 py-2 text-right text-xs text-ink-3">实时</td>
      )}
      {netPnl != null ? (
        <td
          className={cn('px-3 py-2 text-right num font-medium', netPnl >= 0 ? 'text-up' : 'text-down')}
          title={`浮动 PnL ${fmtPnLNumber(unrealizedPnl)} + 库存费 ${fmtPnLNumber(p.swap ?? 0)} + 佣金 ${fmtPnLNumber(p.commission ?? 0)} = 净盈亏 ${fmtPnLNumber(netPnl)}`}
        >
          {fmtPnLNumber(netPnl)}
          {fees !== 0 && <span className="ml-1 text-xs font-normal text-ink-3">(费 {fmtPnLNumber(fees)})</span>}
        </td>
      ) : (
        <td className="px-3 py-2 text-right text-xs text-ink-3">实时</td>
      )}
      <td className="px-3 py-2 text-right">
        <Button
          type="button"
          variant="destructive"
          size="sm"
          onClick={() => onClose(p)}
          title="人工平掉这一笔真实仓(并全量脱钩);不停机、不锁交易"
        >
          <ShieldAlert className="h-4 w-4" />
          平掉此仓
        </Button>
      </td>
    </tr>
  );
}

// ─── 真实账户平仓 + 全量脱钩:两步确认弹窗 ─────────────────────────
//
// Step1 风险知情:必须在点击前显示「将脱钩 M 条持仓、未对冲名义敞口 X 美元」——
// 打标范围恒为全量(不随平仓范围收窄),这是管理员唯一的风险知情途径,故还要显式勾选。
// Step2 原因(必填 ≥4 字)。Step3 只展示结果:失败明细平铺 + note,
// 【不提供重试按钮】—— 重试会与后台 30s 净额对账的自动平仓重复下单。

function CloseRealPositionsDialog({
  target,
  onDismiss,
}: {
  target: CloseTarget | null;
  onDismiss: () => void;
}) {
  const qc = useQueryClient();
  const [step, setStep] = useState<'warn' | 'confirm' | 'result'>('warn');
  const [ack, setAck] = useState(false);
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<CloseRealPositionsResponse | null>(null);

  const open = target != null;
  // 脱钩范围恒为全量存量活跃腿 → 用用户虚拟持仓估 M 与未对冲名义敞口(与大屏同 queryKey,走缓存)
  const userPositionsQ = useQuery({
    queryKey: ['admin', 'user-positions'],
    queryFn: adminMainAccount.userPositions,
    enabled: open,
  });
  const symbolsQ = useQuery({ queryKey: ['admin', 'symbols'], queryFn: adminSymbols.list, enabled: open });

  const legs = (userPositionsQ.data?.items ?? []).filter((p) => !p.detached);
  const legsCount = legs.length;
  // 名义敞口 = Σ 手数 × 合约规模 × 入场价;任一品种缺 contractSize 即整体不可算(不编造数字)
  const contractSizes = new Map((symbolsQ.data ?? []).map((s) => [s.symbolId, s.contractSize]));
  const notionalUnavailable = legs.some((p) => contractSizes.get(p.symbolId) == null);
  const notionalUsd = notionalUnavailable
    ? null
    : legs.reduce((sum, p) => sum + (p.volume / 100) * (contractSizes.get(p.symbolId) ?? 0) * p.entryPrice, 0);

  const reset = () => {
    setStep('warn');
    setAck(false);
    setReason('');
    setResult(null);
  };
  const dismiss = () => {
    onDismiss();
    reset();
  };

  const submit = useMutation({
    mutationFn: () => {
      if (!target) throw new Error('no target');
      const body: CloseRealPositionsRequest = {
        scope: target.scope,
        acknowledgeFullDetach: true,
        reason: reason.trim(),
      };
      if (target.scope === 'position') body.positionIds = [target.position.positionId];
      return adminRealAccount.closeRealPositions(body);
    },
    onSuccess: (res) => {
      setResult(res);
      setStep('result');
      toast.success('真实账户平仓已提交', {
        description: `已脱钩 ${res.legsDetached} 条用户持仓 · 已平 ${res.positionsClosed}/${res.positionsRequested} 笔真实仓`,
      });
      void qc.invalidateQueries({ queryKey: ['admin', 'user-positions'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'main-account', 'positions'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'real-account', 'capacity'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'platform-pnl'] });
    },
    onError: (err) => {
      toast.error('真实账户平仓失败', {
        description: err instanceof ApiError ? err.message : '请稍后重试',
      });
    },
  });

  const busy = submit.isPending;
  const reasonOk = reason.trim().length >= 4;
  const scopeLabel = target?.scope === 'position' ? '指定 1 笔真实仓' : '全部真实仓';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && dismiss()}>
      <DialogContent className="max-w-xl">
        {step === 'warn' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-warn">
                <ShieldAlert className="h-5 w-5" />
                真实账户平仓 · 第 1 步,共 2 步
              </DialogTitle>
              <DialogDescription>
                与「一键强制平仓」不同:本操作不会停机、不会锁定交易,系统继续运行。
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-md border border-line bg-bg-2 p-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <KV label="平仓范围" value={scopeLabel} />
                <KV
                  label="目标仓位"
                  value={target?.scope === 'position' ? target.position.positionId : '全部'}
                />
                <KV
                  label="品种"
                  value={target?.scope === 'position' ? target.position.symbolId : '全部品种'}
                />
                <KV
                  label="手数"
                  value={target?.scope === 'position' ? (target.position.volume / 100).toFixed(2) : '全部'}
                />
                <KV label="将脱钩持仓" value={userPositionsQ.isLoading ? '加载中…' : `${legsCount} 条`} />
                <KV
                  label="未对冲名义敞口"
                  value={
                    symbolsQ.isLoading || userPositionsQ.isLoading
                      ? '加载中…'
                      : notionalUsd == null
                        ? '—(缺合约规模,不可算)'
                        : fmtMoney({ value: Math.round(notionalUsd * 100), digits: 2 })
                  }
                />
              </div>
            </div>

            <div className="rounded-md border border-line bg-bg-3 p-3 text-xs leading-relaxed text-ink-2 space-y-2">
              <p>
                本操作将立即平掉真实账户上的选定仓位,并把当前仍在对冲的 {legsCount} 条用户持仓标记为「已脱钩」。
              </p>
              <div>
                脱钩后:
                <ul className="mt-1 list-disc pl-5 space-y-0.5">
                  <li>用户持仓不会被平,用户可继续持有并自行平仓;</li>
                  <li>这些持仓平仓时不再向真实账户发单,平台承担其全部盈亏;</li>
                  <li>之后新增的用户订单仍会照常在真实账户对冲;</li>
                  <li>真实账户与用户账本的对账口径将不再一致,基差/容量/胜率等指标会出现预期内的偏差。</li>
                </ul>
              </div>
              <p>本操作不可撤销,不会停机、不会锁定交易。</p>
            </div>

            <label className="flex items-start gap-2 text-sm text-ink-1">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0"
                checked={ack}
                onChange={(e) => setAck(e.target.checked)}
              />
              <span>
                我确认:脱钩范围是<span className="font-medium text-down">此刻全部存量持仓</span>
                (共 {legsCount} 条),不只是本次所平的品种或那一笔仓。
              </span>
            </label>

            <div className="text-sm font-medium text-down">
              不可撤销 · 平台将对 {legsCount} 条持仓承担裸敞口
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={dismiss}>取消</Button>
              <Button variant="destructive" onClick={() => setStep('confirm')} disabled={!ack}>
                继续(下一步填写原因)
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'confirm' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-warn">
                <ShieldAlert className="h-5 w-5" />
                真实账户平仓 · 第 2 步,共 2 步
              </DialogTitle>
              <DialogDescription>
                请填写本次人工平仓的操作原因(至少 4 字,进审计日志)。
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-md border border-line bg-bg-2 p-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <KV label="平仓范围" value={scopeLabel} />
                <KV label="将脱钩持仓" value={`${legsCount} 条`} />
              </div>
            </div>

            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="detach-close-reason">操作原因(至少 4 字,仅内部审计)</Label>
                <Input
                  id="detach-close-reason"
                  placeholder="例如:人工判断行情反转,先平真实仓保住利润"
                  value={reason}
                  onChange={(e) => setReason(e.target.value.slice(0, 256))}
                  disabled={busy}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep('warn')} disabled={busy}>上一步</Button>
              <Button
                variant="destructive"
                onClick={() => submit.mutate()}
                disabled={busy || !reasonOk}
              >
                <ShieldAlert className="h-4 w-4" />
                {busy ? '处理中…' : '确认平仓并脱钩'}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'result' && result && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldAlert className="h-5 w-5 text-warn" />
                真实账户平仓已执行
              </DialogTitle>
              <DialogDescription>批次 {result.batchId}</DialogDescription>
            </DialogHeader>

            <div className="rounded-md border border-line bg-bg-2 p-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <KV label="已脱钩持仓" value={`${result.legsDetached} 条`} />
                <KV label="请求平仓" value={`${result.positionsRequested} 笔`} />
                <KV label="确认已平" value={`${result.positionsClosed} 笔`} />
                <KV label="失败" value={`${result.failures.length} 笔`} />
              </div>
            </div>

            {result.failures.length > 0 && (
              <div className="rounded-md border border-line bg-bg-3 p-3 text-xs space-y-1.5">
                <div className="text-ink-2">失败明细(不提供重试:重试会与净额对账的自动平仓重复下单)</div>
                {result.failures.map((f) => (
                  <div key={f.positionId} className="flex items-start justify-between gap-3">
                    <span className="num text-ink-1">
                      {f.positionId}
                      {f.symbolId ? ` · 品种 ${f.symbolId}` : ''}
                    </span>
                    <span className="text-right text-down">
                      {f.code} · {f.message}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="text-xs text-ink-3">{result.note}</div>

            <DialogFooter>
              <Button variant="outline" onClick={dismiss}>关闭</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KV({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-ink-3">{label}</div>
      <div className="num text-ink-1">{value}</div>
    </div>
  );
}
